import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import { AuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePlatform, useRunTimeEnvironment, useScript } from '../../utils/hooks';
import { runAdmob, useAdsListener } from '../../services/admob';
import usePunchGame, { POPUP_TYPE } from './usePunchGame';
import {
  getCompleteAdUrl,
  getGetStateUrl,
  getPunchSingleUrl,
  getStartAdUrl,
} from '../../api/generated/game-punch/game-punch.ts';
import {
  PunchAdRestPopup,
  PunchBurningEndPopup,
  PunchBurningStartPopup,
  PunchFreePunchAdPopup,
  PunchHighRewardPopup,
  PunchPointShortagePopup,
} from './PunchPopups';
import { getHighRewardTier, HIGH_REWARD_THRESHOLD, HIGH_REWARD_TIER } from './rewardConfig';
import PunchBottomRewardToast from './PunchBottomRewardToast';
import PunchInfoBottomSheet from './PunchInfoBottomSheet';
import PunchPrizePoolSummary from './PunchPrizePoolSummary';
import PunchRewardBurst from './PunchRewardBurst';
import PunchWinnerTicker from './PunchWinnerTicker';
import BurningStarField from './BurningStarField';
import ArrowLeftImage from '../../assets/icons/arrow_left.png';
import pointIcon from '../../assets/images/img_point.png';
import ToastSparklesImage from '../../assets/icons/punch/toast-sparkles.png';
import PunchFrontImage from '../../assets/icons/punch_front.png';
import PunchBackImage from '../../assets/icons/punch_back.png';
import BenefitPunchImage from '../../assets/icons/punch/benefit-punch.png';
import BenefitBurningImage from '../../assets/icons/punch/benefit-burning.png';
import BenefitMissionImage from '../../assets/icons/punch/benefit-mission.png';
import DividerLineImage from '../../assets/icons/punch/divider-line.svg';
import BurningDividerLineImage from '../../assets/icons/punch/divider-line-burning.svg';
import BurningStartCoinImage from '../../assets/icons/punch/burning-start-coin.png';
import BurningStartSparkleImage from '../../assets/icons/punch/burning-start-sparkle.png';
import BurningStartCenterLightImage from '../../assets/icons/punch/center-light.png';
import HighRewardCrownIcon150 from '../../assets/icons/punch/crown.png';
import HighRewardCrownIcon100 from '../../assets/icons/punch/dia.png';
import HighRewardCrownIcon80 from '../../assets/icons/punch/con.png';
import HighRewardCrownIcon50 from '../../assets/icons/punch/lucky.png';
import PunchCoinBurstImage from '../../assets/icons/punch_coin.png';
import './animations.css';

const DEFAULT_PUNCH_MOCK_MODE = process.env.REACT_APP_PUNCH_MOCK_MODE !== 'false';
const IS_DEV_BUILD = process.env.NODE_ENV === 'development';
const SHOW_PUNCH_MODE_DEBUG_UI = IS_DEV_BUILD;
const PUNCH_MOCK_STORAGE_KEY = 'punch-mock-state-v2';
const PUNCH_API_MODE_STORAGE_KEY = 'punch-api-mode-v1';
const BURNING_START_TEXT_SWITCH_MS = 820;
const PUNCH_CARD_AD_ICON_URL = `${process.env.PUBLIC_URL}/assets/icons/punch/high-reward-ad-icon.svg`;
const PUNCH_CRITICAL_PRELOAD_IMAGES = [PunchFrontImage, PunchBackImage];
const PUNCH_OPTIONAL_PRELOAD_IMAGES = [
  PunchCoinBurstImage,
  HighRewardCrownIcon150,
  HighRewardCrownIcon100,
  HighRewardCrownIcon80,
  HighRewardCrownIcon50,
];

const preloadImage = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve();
      return;
    }
    const img = new Image();
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      resolve();
    };
    img.onload = finish;
    img.onerror = finish;
    img.src = src;
    if (img.complete) finish();
  });
const getServerErrorMessage = (rawPayload, fallbackMessage) => {
  if (typeof rawPayload !== 'string') return fallbackMessage;
  const trimmed = rawPayload.trim();
  if (!trimmed) return fallbackMessage;
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed?.message === 'string' && parsed.message.trim().length > 0) {
      return parsed.message;
    }
  } catch (error) {
    // no-op: 서버가 plain text를 반환하는 경우 fallback 사용
  }
  return fallbackMessage;
};

const formatMmSs = (seconds) => {
  const safe = Math.max(0, seconds);
  return `${String(Math.floor(safe / 60)).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`;
};
const getMilestoneShakeClassByHit = (hit) => {
  if (hit >= 400) return 'punch-screen-shake-max-x3';
  if (hit >= 300) return 'punch-screen-shake-high-x3';
  if (hit >= 200) return 'punch-screen-shake-mid-x3';
  return 'punch-screen-shake-low-x3';
};
const getHighRewardShakeClassByPoint = (point) => {
  if (point >= HIGH_REWARD_THRESHOLD.JACKPOT) return 'punch-screen-shake-max-x3';
  if (point >= HIGH_REWARD_THRESHOLD.GREAT) return 'punch-screen-shake-high-x3';
  if (point >= HIGH_REWARD_THRESHOLD.NICE) return 'punch-screen-shake-mid-x3';
  return 'punch-screen-shake-low-x3';
};
const PUNCH_MOCK_DATA = {
  winners: [
    { name: '하은', point: 80 },
    { name: '유찬', point: 100 },
    { name: '성준', point: 150 },
    { name: '도윤', point: 100 },
    { name: '지안', point: 150 },
    { name: '예준', point: 100 },
    { name: '서윤', point: 150 },
    { name: '민재', point: 100 },
  ],
  summary: {
    roundLabel: '26회차',
    periodLabel: '2026.03.17 ~ 2026.03.19',
    potPoint: 230000,
  },
  benefits: [
    { title: '무료펀치', value: '3회', icon: BenefitPunchImage, tone: 'default' },
    { title: '버닝타임', value: '6회', icon: BenefitBurningImage, tone: 'default' },
    { title: '상금통', value: '미션', icon: BenefitMissionImage, tone: 'highlight' },
  ],
};

const INFO_SHEET_TYPE = Object.freeze({
  GAME: 'game',
  BURNING: 'burning',
  FREE_PUNCH: 'free_punch',
});

const PUNCH_POPUP_AD_PLACEMENT_BY_TYPE = {
  [POPUP_TYPE.AD_REST]: 'mandatory20',
  [POPUP_TYPE.HIGH_REWARD]: 'highReward',
  [POPUP_TYPE.POINT_SHORTAGE]: 'freePunchShortage',
  [POPUP_TYPE.FREE_PUNCH_AD]: 'freePunch',
  [POPUP_TYPE.BURNING_START_AD]: 'burningEnter',
  [POPUP_TYPE.BURNING_END_AD]: 'burningExit',
};

const INFO_SHEET_CONTENT = Object.freeze({
  [INFO_SHEET_TYPE.GAME]: {
    title: '냥냥펀치 이용안내',
    highlights: ['펀치 한 방으로 최대 150포인트!', '한 번의 터치가 대박이 될 수도 있어요.'],
    sections: [
      {
        title: '1. 펀치',
        items: ['펀치 1회당 5포인트 차감 (버닝타임 시 3P)', '펀치 시 확률에 따라 최대 150P 포인트 보상'],
      },
      {
        title: '2. 버닝타임',
        items: [
          '30초간 진행',
          '펀치 1회당 3포인트 차감',
          '진행중 광고 100% 스킵',
          '매일 00:00에 6회 충전',
          '4회차부터 쿨타임 30초',
        ],
      },
      {
        title: '3. 펀치 상금통 안내',
        items: [
          '3일 단위로 회차 진행',
          '1회 펀치 = 1 펀치수',
          '펀치 수에 따라 상금통 미션',
          '최대 냥스코어: 2,421P',
          '순위 및 지급 퍼센트는 랭킹보드에서 확인 가능',
          '회차 마무리 시 냥스코어 TOP 50 랭킹 순위에 따라 쌓인 포인트를 차등 분배',
        ],
      },
    ],
    notices: [
      '쌓인 포인트의 20%는 운영 수수료로 차감됩니다.',
      '상금 지급은 운영 상황에 따라 별도의 안내 없이 변경 또는 중단될 수 있습니다.',
      '부정한 방법으로 참여하거나 운영 정책을 위반한 경우, 지급된 상금이 취소될 수 있습니다.',
    ],
  },
  [INFO_SHEET_TYPE.BURNING]: {
    title: '버닝타임 안내',
    sections: [
      {
        bullet: false,
        items: ['버닝타임 활성화 시 30초간 광고 100% 스킵!', '매일 00:00에 6회 충전 (4회차부터 쿨타임 30초)'],
      },
    ],
  },
  [INFO_SHEET_TYPE.FREE_PUNCH]: {
    title: '무료펀치·버닝타임 스케줄',
    sections: [
      {
        title: '[ 무료펀치 ]',
        items: ['매일 00:00에 3회 충전', '1회 사용 시 쿨타임 30초'],
      },
      {
        title: '[ 버닝타임 ]',
        items: [
          '매일 00:00에 6회 충전',
          '1회 지속시간 1분',
          '버닝타임 동안은 광고 100% 스킵',
          '4회차부터는 쿨타임 30초 존재',
        ],
      },
    ],
  },
});

const MAIN_REWARD_BACKDROP_DURATION_MS = 1150;

// 보상 포인트 → 이펙트 variant 단일 매핑 함수
const getRewardEffectVariant = (rewardPoint) => {
  if (rewardPoint >= 150) return 'rainbow';
  if (rewardPoint >= 100) return 'purple';
  if (rewardPoint >= 80) return 'gold';
  if (rewardPoint >= 50) return 'emerald';
  return null;
};

const BACKDROP_BASE_CLASS =
  'punch-main-reward-backdrop absolute -inset-x-[10px] -inset-y-[8px] z-[-1] pointer-events-none overflow-hidden';

function PunchMainRewardBackdrop({ effect }) {
  const variant = effect?.variant;
  if (!variant || !effect?.id) return null;

  // 50P — 에메랄드 웨이브 (스파크는 PunchRewardRing에서 section 안에 렌더링)
  if (variant === 'emerald') {
    return (
      <div key={effect.id} className={`${BACKDROP_BASE_CLASS} punch-effect-preview-emerald`}>
        <div className="punch-effect-emerald-wave" />
      </div>
    );
  }

  // 80P — 골드 스파클 + 별
  if (variant === 'gold') {
    return (
      <div key={effect.id} className={`${BACKDROP_BASE_CLASS} punch-effect-preview-gold`}>
        <span className="punch-effect-gold-star punch-effect-gold-star-1">✦</span>
        <span className="punch-effect-gold-star punch-effect-gold-star-2">✦</span>
        <span className="punch-effect-gold-star punch-effect-gold-star-3">✦</span>
        <span className="punch-effect-gold-star punch-effect-gold-star-4">★</span>
        <span className="punch-effect-gold-star punch-effect-gold-star-5">★</span>
      </div>
    );
  }

  // 100P — 퍼플 유성우 + 보라 웨이브 + 다이아몬드
  if (variant === 'purple') {
    return (
      <div key={effect.id} className={`${BACKDROP_BASE_CLASS} punch-effect-preview-purple`}>
        <div className="punch-effect-purple-wave" />
        <div className="punch-effect-purple-meteor punch-effect-purple-meteor-1" />
        <div className="punch-effect-purple-meteor punch-effect-purple-meteor-2" />
        <div className="punch-effect-purple-meteor punch-effect-purple-meteor-3" />
        <div className="punch-effect-purple-meteor punch-effect-purple-meteor-4" />
        <div className="punch-effect-purple-meteor punch-effect-purple-meteor-5" />
        <div className="punch-effect-purple-meteor punch-effect-purple-meteor-6" />
        <img src={HighRewardCrownIcon100} alt="" className="punch-effect-purple-diamond h-8 w-8 object-contain" />
      </div>
    );
  }

  // 150P — 레인보우 폭발 + 컨페티 + 플래시 + 왕관 낙하
  return (
    <div key={effect.id} className={`${BACKDROP_BASE_CLASS} punch-effect-preview-rainbow`}>
      <div className="punch-effect-rainbow-flash" />
      <div className="punch-effect-preview-confetti punch-effect-preview-confetti-1" />
      <div className="punch-effect-preview-confetti punch-effect-preview-confetti-2" />
      <div className="punch-effect-preview-confetti punch-effect-preview-confetti-3" />
      <img
        src={HighRewardCrownIcon150}
        alt=""
        className="punch-effect-preview-crown punch-effect-preview-crown-1 h-5 w-5 object-contain"
      />
      <img
        src={HighRewardCrownIcon150}
        alt=""
        className="punch-effect-preview-crown punch-effect-preview-crown-2 h-5 w-5 object-contain"
      />
    </div>
  );
}

function PunchRewardRing({ variant }) {
  // 100P(purple)는 링/스파크 없음 — 유성우+웨이브만 표시
  if (!variant || variant === 'purple') return null;

  if (variant === 'emerald') {
    return (
      <>
        {/* 정적 링 */}
        <div className="punch-reward-ring punch-reward-ring--emerald" />
        {/* 스파크: backdrop(z-[-1]) 안에 넣으면 z-[3] 레이어에 가려지므로 section 안에서 렌더링 */}
        <span className="punch-effect-emerald-spark punch-effect-emerald-spark-1 z-[2]">✦</span>
        <span className="punch-effect-emerald-spark punch-effect-emerald-spark-2 z-[2]">✦</span>
        <span className="punch-effect-emerald-spark punch-effect-emerald-spark-3 z-[2]">✦</span>
      </>
    );
  }

  if (variant === 'rainbow') {
    return (
      <>
        <div className="punch-reward-ring punch-reward-ring--rainbow" />
        <div className="punch-reward-ring punch-reward-ring--rainbow punch-reward-ring--rainbow-b" />
      </>
    );
  }

  return <div className={`punch-reward-ring punch-reward-ring--${variant}`} />;
}

function PunchTopBar({ isBurningActive = false, onOpenGuide }) {
  const navigate = useNavigate();

  return (
    <header className="flex h-[46px] items-center justify-between px-3 pl-[10px]" aria-label="상단 네비게이션">
      <button
        type="button"
        className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0"
        onClick={() => navigate(-1)}
        aria-label="뒤로가기"
      >
        <img
          src={ArrowLeftImage}
          alt=""
          className={`h-6 w-6 object-contain ${isBurningActive ? 'brightness-0 invert' : ''}`}
        />
      </button>
      <button
        type="button"
        onClick={onOpenGuide}
        aria-label="이용 안내 열기"
        className={`border-0 bg-transparent p-0 text-center font-['Pretendard'] text-sm font-bold leading-[21px] ${
          isBurningActive ? 'text-white' : 'text-gray05'
        }`}
      >
        안내
      </button>
    </header>
  );
}

function PunchBenefitCard({
  title,
  value,
  icon,
  tone,
  disabled = false,
  onInfoClick,
  adIconSrc = null,
  isValueLoading = false,
}) {
  const isClickable = typeof tone === 'string' && tone.includes('clickable');
  const isInteractive = isClickable && !disabled && !isValueLoading;
  const isObjectValue = value && typeof value === 'object';
  const clickableHandler = isObjectValue && typeof value.onClick === 'function' ? value.onClick : undefined;
  const label = isValueLoading ? '로딩 중' : isObjectValue ? value.label : value;
  const hasInfoButton = typeof onInfoClick === 'function';

  return (
    <div
      className={`relative flex aspect-[106/108] w-full flex-col items-center justify-center gap-2 rounded-2xl border-0 shadow-[0px_5px_16px_0px_rgba(0,0,0,0.1)] ${
        tone.includes('highlight') ? 'bg-[#FFF0E8]' : 'bg-white'
      } ${isInteractive ? 'cursor-pointer' : ''} ${disabled ? 'bg-[#E2E2E2] text-gray03' : ''}`}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? clickableHandler : undefined}
      onKeyDown={
        isInteractive && clickableHandler
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                clickableHandler();
              }
            }
          : undefined
      }
    >
      {hasInfoButton ? (
        <button
          type="button"
          aria-label={`${title} 안내`}
          onClick={(event) => {
            event.stopPropagation();
            onInfoClick();
          }}
          className="punch-benefit-info-button absolute right-2 top-2 flex h-[18px] w-[18px] items-center justify-center rounded-full text-[11px] font-extrabold leading-none text-[#7B7B7B]"
        >
          ?
        </button>
      ) : null}
      <img src={icon} alt={title} className="aspect-square w-[37.7%] object-contain" />
      <p
        className={`m-0 font-['Pretendard'] text-sm font-normal leading-[18px] ${disabled ? 'text-gray03' : 'text-black'}`}
      >
        {title}
      </p>
      <p
        className={`-mt-2 m-0 font-['Pretendard'] text-sm font-extrabold leading-[18px] ${disabled ? 'text-gray03' : 'text-black'}`}
      >
        <span className="inline-flex items-center gap-[3px] mt-1">
          {adIconSrc ? (
            <img
              src={adIconSrc}
              alt=""
              aria-hidden="true"
              className={`h-[13px] w-[16px] object-contain ${disabled ? 'opacity-40' : ''}`}
            />
          ) : null}
          <span>{label}</span>
        </span>
      </p>
    </div>
  );
}

function PunchBottomPoint({ point }) {
  return (
    <footer className="fixed inset-x-0 bottom-0 z-30 mx-auto flex h-16 w-full max-w-[500px] box-border items-center justify-between bg-[#FFFFFF] px-5 shadow-[0_0_10px_rgba(0,0,0,0.05)]">
      <span className="text-center font-['Pretendard'] text-xs font-extrabold leading-[18px] tracking-[-0.3px] text-gray03">
        보유포인트
      </span>
      <div className="inline-flex items-center gap-1">
        <img src={pointIcon} alt="포인트 코인" className="h-6 w-6 object-cover" />
        <strong className="font-['Pretendard'] text-[18px] font-bold leading-none tracking-[-0.5px] text-gray05">
          {point.toLocaleString()}
        </strong>
      </div>
    </footer>
  );
}

export default function PunchPage() {
  const RTE = useRunTimeEnvironment();
  const browserDeviceType = usePlatform();
  useScript(`${process.env.PUBLIC_URL}/flutter/flutter_script.js`);
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof window.notifyAds === 'function') return;
    window.notifyAds = (event, reward) => {
      window.dispatchEvent(
        new CustomEvent('ads', {
          detail: { event, reward },
        }),
      );
    };
  }, []);
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken, appInfo } = useContext(AuthContext);
  const deviceType = appInfo?.os === 'ios' || appInfo?.os === 'android' ? appInfo.os : browserDeviceType;
  const { showErrorToast } = useToast();
  const accessToken = useAccessToken();
  const [isPunchMockModeEnabled, setIsPunchMockModeEnabled] = useState(() => {
    if (!SHOW_PUNCH_MODE_DEBUG_UI) return false;
    try {
      const savedMode = window.localStorage.getItem(PUNCH_API_MODE_STORAGE_KEY);
      if (savedMode === 'mock') return true;
      if (savedMode === 'api') return false;
    } catch (error) {
      // no-op
    }
    return DEFAULT_PUNCH_MOCK_MODE;
  });
  const [displayData] = useState(PUNCH_MOCK_DATA);
  const [freePunchHint, setFreePunchHint] = useState('');
  const [burningHint, setBurningHint] = useState('');
  const [activeInfoSheetType, setActiveInfoSheetType] = useState(null);
  const [activeShakeClass, setActiveShakeClass] = useState('');
  const [mainRewardBackdropEffect, setMainRewardBackdropEffect] = useState(null);
  const [isPendingFreePunchAd, setIsPendingFreePunchAd] = useState(false);
  const [isPendingPunchAd, setIsPendingPunchAd] = useState(false);
  const [punchAdLoadingPlacement, setPunchAdLoadingPlacement] = useState(null);
  const [livePrizePoolAmountFromPlay, setLivePrizePoolAmountFromPlay] = useState(null);
  const [isPunchCoreAssetsReady, setIsPunchCoreAssetsReady] = useState(false);
  const pendingAdPlacementRef = useRef(null);
  const pendingAdCallbackRef = useRef(null);
  // null = 초기/완료, false = start 실패(complete 스킵), string = 유효 토큰
  const pendingAdTokenRef = useRef(null);
  const pendingFreePunchAdTokenRef = useRef(null);
  const [burningStartTextPhase, setBurningStartTextPhase] = useState(0);
  const shakeRafRef = useRef(null);
  const shakeResetTimerRef = useRef(null);
  const gamePunchStateRefreshTimerRef = useRef(null);
  const punchAudioCtxRef = useRef(null);
  const punchAudioThrottleRef = useRef(0);
  const punchNoiseBufferRef = useRef({ normal: null, burning: null, jackpot: null, sampleRate: null });

  const getPunchAudioCtx = useCallback(() => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!punchAudioCtxRef.current) {
      punchAudioCtxRef.current = new Ctx();
    }
    const ctx = punchAudioCtxRef.current;
    if (ctx?.state === 'suspended') {
      // iOS/Safari: 사용자 제스처에서 resume 필요
      void ctx.resume().catch(() => null);
    }
    return ctx;
  }, []);

  const playPunchSfx = useCallback(
    (variant = 'normal') => {
      const nowMs = Date.now();
      if (nowMs - punchAudioThrottleRef.current < 45) return;
      punchAudioThrottleRef.current = nowMs;

      const ctx = getPunchAudioCtx();
      if (!ctx) return;

      const now = ctx.currentTime;

      // 1) 타격감 — 짧은 노이즈 버스트
      const noiseLen = variant === 'burning' ? 0.08 : 0.06;
      if (punchNoiseBufferRef.current.sampleRate !== ctx.sampleRate) {
        punchNoiseBufferRef.current = { normal: null, burning: null, jackpot: null, sampleRate: ctx.sampleRate };
      }
      const cacheKey = variant === 'burning' ? 'burning' : 'normal';
      if (!punchNoiseBufferRef.current[cacheKey]) {
        const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * noiseLen), ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i += 1) {
          const t = 1 - i / data.length;
          const curve = variant === 'burning' ? Math.pow(t, 2) : Math.pow(t, 3);
          data[i] = (Math.random() * 2 - 1) * curve;
        }
        punchNoiseBufferRef.current[cacheKey] = buf;
      }
      const noise = ctx.createBufferSource();
      noise.buffer = punchNoiseBufferRef.current[cacheKey];
      const noiseFilter = ctx.createBiquadFilter();
      noiseFilter.type = 'bandpass';
      noiseFilter.frequency.value = variant === 'burning' ? 1200 : 800;
      noiseFilter.Q.value = variant === 'burning' ? 0.8 : 1.2;
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(variant === 'burning' ? 0.4 : 0.35, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.001, now + noiseLen);
      noise.connect(noiseFilter).connect(noiseGain).connect(ctx.destination);
      noise.start(now);

      // 2) 묵직한 톤 — 저주파 펀치감
      const osc = ctx.createOscillator();
      osc.type = variant === 'burning' ? 'sawtooth' : 'sine';
      osc.frequency.setValueAtTime(variant === 'burning' ? 220 : 180, now);
      osc.frequency.exponentialRampToValueAtTime(
        variant === 'burning' ? 50 : 60,
        now + (variant === 'burning' ? 0.1 : 0.08),
      );
      const oscGain = ctx.createGain();
      oscGain.gain.setValueAtTime(variant === 'burning' ? 0.3 : 0.4, now);
      oscGain.gain.exponentialRampToValueAtTime(0.001, now + (variant === 'burning' ? 0.12 : 0.1));
      osc.connect(oscGain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + (variant === 'burning' ? 0.15 : 0.12));

      // 3) 살짝 클릭감 — 고주파 틱(일반 펀치에서만)
      if (variant !== 'burning') {
        const click = ctx.createOscillator();
        click.type = 'square';
        click.frequency.value = 1200;
        const clickGain = ctx.createGain();
        clickGain.gain.setValueAtTime(0.08, now);
        clickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.02);
        click.connect(clickGain).connect(ctx.destination);
        click.start(now);
        click.stop(now + 0.03);
      }
    },
    [getPunchAudioCtx],
  );

  const playHighRewardSfx = useCallback(
    (rewardPoint) => {
      const tier = getHighRewardTier(rewardPoint);
      if (tier === HIGH_REWARD_TIER.NONE) return;

      const ctx = getPunchAudioCtx();
      if (!ctx) return;

      const now = ctx.currentTime;

      // 등급별 아르페지오 설정
      const TIER_CONFIG = {
        [HIGH_REWARD_TIER.BASIC]: { freqs: [523, 659, 784], gainPeak: 0.18, noteDur: 0.22, step: 0.09 },
        [HIGH_REWARD_TIER.NICE]: { freqs: [523, 659, 784, 1047], gainPeak: 0.22, noteDur: 0.26, step: 0.09 },
        [HIGH_REWARD_TIER.GREAT]: { freqs: [523, 659, 784, 1047, 1319], gainPeak: 0.26, noteDur: 0.3, step: 0.085 },
        [HIGH_REWARD_TIER.JACKPOT]: {
          freqs: [523, 659, 784, 1047, 1319, 1568],
          gainPeak: 0.3,
          noteDur: 0.34,
          step: 0.08,
        },
      };

      const { freqs, gainPeak, noteDur, step } = TIER_CONFIG[tier];

      freqs.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;

        const gain = ctx.createGain();
        const noteStart = now + i * step;
        gain.gain.setValueAtTime(0, noteStart);
        gain.gain.linearRampToValueAtTime(gainPeak, noteStart + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, noteStart + noteDur);

        osc.connect(gain).connect(ctx.destination);
        osc.start(noteStart);
        osc.stop(noteStart + noteDur + 0.01);
      });

      // JACKPOT 전용: 마지막 음 직후 코인 떨어지는 짧은 노이즈 버스트
      if (tier === HIGH_REWARD_TIER.JACKPOT) {
        const burstStart = now + freqs.length * step + 0.05;
        const burstLen = 0.18;
        if (punchNoiseBufferRef.current.sampleRate !== ctx.sampleRate) {
          punchNoiseBufferRef.current = { normal: null, burning: null, jackpot: null, sampleRate: ctx.sampleRate };
        }
        if (!punchNoiseBufferRef.current.jackpot) {
          const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * burstLen), ctx.sampleRate);
          const data = buf.getChannelData(0);
          for (let i = 0; i < data.length; i += 1) {
            data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2.5);
          }
          punchNoiseBufferRef.current.jackpot = buf;
        }
        const src = ctx.createBufferSource();
        src.buffer = punchNoiseBufferRef.current.jackpot;
        const filter = ctx.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.value = 2400;
        const burstGain = ctx.createGain();
        burstGain.gain.setValueAtTime(0.28, burstStart);
        burstGain.gain.exponentialRampToValueAtTime(0.001, burstStart + burstLen);
        src.connect(filter).connect(burstGain).connect(ctx.destination);
        src.start(burstStart);
      }
    },
    [getPunchAudioCtx],
  );

  const fetchProfileInfo = useCallback(
    async (...args) => {
      try {
        const response = await fetchWithToken(...args);
        return response;
      } catch (error) {
        console.error('backend api: error GET /user/profile/info', { error });
        throw error;
      }
    },
    [fetchWithToken],
  );
  const { data: profileInfo, mutate: mutateProfileInfo } = useSWR(
    accessToken && [`${API_SERVER_DOMAIN}/user/profile/info`, accessToken],
    fetchProfileInfo,
    { dedupingInterval: 0, revalidateOnMount: true },
  );
  const fetchGamePunchState = useCallback(
    async (...args) => {
      try {
        const response = await fetchWithToken(...args);
        return response;
      } catch (error) {
        console.error('backend api: error GET /punch/state', { error });
        throw error;
      }
    },
    [fetchWithToken],
  );
  const { data: gamePunchState, mutate: mutateGamePunchState } = useSWR(
    accessToken && !isPunchMockModeEnabled && [`${API_SERVER_DOMAIN}${getGetStateUrl()}`, accessToken],
    fetchGamePunchState,
    { dedupingInterval: 0, revalidateOnMount: true },
  );
  const scheduleGamePunchStateRefresh = useCallback(
    (delayMs = 250) => {
      if (isPunchMockModeEnabled || !accessToken) return;
      if (gamePunchStateRefreshTimerRef.current) {
        window.clearTimeout(gamePunchStateRefreshTimerRef.current);
      }
      gamePunchStateRefreshTimerRef.current = window.setTimeout(() => {
        mutateGamePunchState();
        mutateProfileInfo();
        gamePunchStateRefreshTimerRef.current = null;
      }, delayMs);
    },
    [accessToken, isPunchMockModeEnabled, mutateGamePunchState, mutateProfileInfo],
  );
  const sessionIdRef = useRef(`sess-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const sendPunchPlayToBackend = useCallback(
    async ({ mode, eventId }) => {
      if (!accessToken) throw new Error('NO_ACCESS_TOKEN');
      const url = `${API_SERVER_DOMAIN}${getPunchSingleUrl()}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          eventId,
          mode,
          count: 1,
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const err = new Error('PUNCH_PLAY_FAILED');
        err.status = response.status;
        err.payload = payload;
        throw err;
      }
      return payload;
    },
    [API_SERVER_DOMAIN, accessToken],
  );

  const sendStartAdToBackend = useCallback(
    async (placement) => {
      if (!accessToken || !placement) return null;
      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getStartAdUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ placement, deviceType }),
        });
        if (!response.ok) {
          const payload = await response.text().catch(() => '');
          const message = getServerErrorMessage(payload, '광고를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
          showErrorToast(message);
          throw new Error(`PUNCH_START_AD_FAILED:${response.status}:${payload}`);
        }
        const data = await response.json().catch(() => null);
        return {
          adToken: data?.adToken ?? null,
          placementId: data?.placementId ?? '',
        };
      } catch (error) {
        console.error('backend api: error POST /punch/ads/start', { error });
        if (!String(error?.message || '').startsWith('PUNCH_START_AD_FAILED')) {
          showErrorToast('광고를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
        }
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, deviceType, showErrorToast],
  );

  const sendCompleteAdToBackend = useCallback(
    async (placement, adToken) => {
      if (!accessToken) return false;
      // start가 호출됐지만 실패한 경우 complete 스킵
      if (adToken === false) {
        console.warn('punch ad: skipping complete because start failed', { placement });
        return false;
      }
      try {
        const body = adToken ? { placement, adToken } : { placement };
        const response = await fetch(`${API_SERVER_DOMAIN}${getCompleteAdUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const payload = await response.text().catch(() => '');
          const message = getServerErrorMessage(payload, '광고 보상 처리에 실패했어요. 다시 시도해 주세요.');
          showErrorToast(message);
          throw new Error(`PUNCH_COMPLETE_AD_FAILED:${response.status}:${payload}`);
        }
        return await response.json().catch(() => ({}));
      } catch (error) {
        console.error('backend api: error POST /punch/ads/complete', { error });
        if (!String(error?.message || '').startsWith('PUNCH_COMPLETE_AD_FAILED')) {
          showErrorToast('광고 보상 처리에 실패했어요. 다시 시도해 주세요.');
        }
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, showErrorToast],
  );

  const {
    isPunching,
    isBurningActive,
    currentPoint,
    activePopup,
    freePunchRemain,
    freePunchDailyMax,
    freePunchCooldownSec,
    burningRemain,
    burningDailyMax,
    burningDurationSec,
    burningCooldownSec,
    burningHitCount,
    burningMilestoneEvent,
    burningStartEffectEvent,
    floatingToastEvent,
    bottomToastEvents,
    rewardBurstEvent,
    handlePunch,
    handleUseFreePunch,
    handleUseBurning,
    handleWatchAdForRest,
    handleWatchAdForHighReward,
    handleSkipHighReward,
    handleWatchAdForBurningStart,
    handleWatchAdForBurningEnd,
    handleSkipBurningEndAd,
    handleWatchAdForPointShortage,
    handleChargeForPointShortage,
    handleWatchAdForFreePunch,
    closeCurrentPopup,
  } = usePunchGame(profileInfo?.info?.point, {
    initState: isPunchMockModeEnabled ? null : gamePunchState,
    onPunchPlay: isPunchMockModeEnabled ? null : sendPunchPlayToBackend,
    onPunchResult: isPunchMockModeEnabled
      ? null
      : (result) => {
          const amount = Number(result?.prizePoolAmount);
          if (Number.isFinite(amount)) {
            setLivePrizePoolAmountFromPlay(amount);
          }
        },
  });

  const clearPunchAdLoading = useCallback(() => {
    setPunchAdLoadingPlacement(null);
  }, []);

  const handlePunchAdsError = useCallback(
    (errorInfo = {}) => {
      pendingFreePunchAdTokenRef.current = null;
      pendingAdPlacementRef.current = null;
      pendingAdCallbackRef.current = null;
      pendingAdTokenRef.current = null;
      setIsPendingFreePunchAd(false);
      setIsPendingPunchAd(false);
      clearPunchAdLoading();
      const message =
        typeof errorInfo?.message === 'string' && errorInfo.message.trim().length > 0
          ? errorInfo.message
          : '광고 재생에 실패했어요. 잠시 후 다시 시도해 주세요.';
      showErrorToast(message);
      scheduleGamePunchStateRefresh();
    },
    [clearPunchAdLoading, scheduleGamePunchStateRefresh, showErrorToast],
  );

  useAdsListener(
    '',
    '',
    { task: 'punch-free-punch', pending: isPendingFreePunchAd, silentErrorAlert: true, onError: handlePunchAdsError },
    async () => {
      if (!isPendingFreePunchAd) return;
      const adToken = pendingFreePunchAdTokenRef.current;
      pendingFreePunchAdTokenRef.current = null;
      setIsPendingFreePunchAd(false);
      clearPunchAdLoading();
      let completeResult = true;
      if (!isPunchMockModeEnabled) {
        completeResult = await sendCompleteAdToBackend('freePunch', adToken);
      }
      if (!completeResult) {
        scheduleGamePunchStateRefresh();
        return;
      }
      handleWatchAdForFreePunch();
      scheduleGamePunchStateRefresh();
    },
  );

  useAdsListener(
    '',
    '',
    { task: 'punch-ad', pending: isPendingPunchAd, silentErrorAlert: true, onError: handlePunchAdsError },
    async () => {
      if (!isPendingPunchAd) return;
      const placement = pendingAdPlacementRef.current;
      const callback = pendingAdCallbackRef.current;
      const adToken = pendingAdTokenRef.current;
      setIsPendingPunchAd(false);
      clearPunchAdLoading();
      pendingAdPlacementRef.current = null;
      pendingAdCallbackRef.current = null;
      pendingAdTokenRef.current = null;
      let completeResult = true;
      if (!isPunchMockModeEnabled && placement) {
        completeResult = await sendCompleteAdToBackend(placement, adToken);
      }
      if (!completeResult) {
        scheduleGamePunchStateRefresh();
        return;
      }
      callback?.(completeResult);
    },
  );

  const triggerPunchAd = useCallback(
    async (placement, onComplete) => {
      if (!isPunchMockModeEnabled && placement && punchAdLoadingPlacement) return;

      pendingAdPlacementRef.current = placement;
      pendingAdCallbackRef.current = onComplete;
      pendingAdTokenRef.current = null;

      if (!isPunchMockModeEnabled && placement) {
        setPunchAdLoadingPlacement(placement);
        try {
          const adStart = await sendStartAdToBackend(placement);
          if (adStart === false) {
            pendingAdPlacementRef.current = null;
            pendingAdCallbackRef.current = null;
            clearPunchAdLoading();
            return;
          }
          pendingAdTokenRef.current = adStart?.adToken ?? null;
          if (typeof window.flutter_inappwebview?.callHandler !== 'function') {
            const completeResult = await sendCompleteAdToBackend(placement, pendingAdTokenRef.current);
            pendingAdPlacementRef.current = null;
            pendingAdCallbackRef.current = null;
            pendingAdTokenRef.current = null;
            clearPunchAdLoading();
            if (!completeResult) {
              scheduleGamePunchStateRefresh();
              return;
            }
            onComplete?.(completeResult);
            return;
          }
          setIsPendingPunchAd(true);
          runAdmob('', adStart?.placementId ?? '');
          return;
        } catch (error) {
          pendingAdPlacementRef.current = null;
          pendingAdCallbackRef.current = null;
          pendingAdTokenRef.current = null;
          clearPunchAdLoading();
          throw error;
        }
      }

      setIsPendingPunchAd(true);
      runAdmob();
    },
    [
      clearPunchAdLoading,
      isPunchMockModeEnabled,
      punchAdLoadingPlacement,
      scheduleGamePunchStateRefresh,
      sendCompleteAdToBackend,
      sendStartAdToBackend,
    ],
  );

  const handleFreePunchAdWatch = useCallback(async () => {
    if (punchAdLoadingPlacement) return;
    if (RTE !== 'APP') {
      handleWatchAdForFreePunch();
      scheduleGamePunchStateRefresh();
      return;
    }
    pendingFreePunchAdTokenRef.current = null;
    if (!isPunchMockModeEnabled) {
      setPunchAdLoadingPlacement('freePunch');
      try {
        const adStart = await sendStartAdToBackend('freePunch');
        if (adStart === false) {
          clearPunchAdLoading();
          return;
        }
        pendingFreePunchAdTokenRef.current = adStart?.adToken ?? null;
        setIsPendingFreePunchAd(true);
        runAdmob('', adStart?.placementId ?? '');
        return;
      } catch (error) {
        clearPunchAdLoading();
        throw error;
      }
    }
    setIsPendingFreePunchAd(true);
    runAdmob();
  }, [
    RTE,
    clearPunchAdLoading,
    handleWatchAdForFreePunch,
    isPunchMockModeEnabled,
    punchAdLoadingPlacement,
    scheduleGamePunchStateRefresh,
    sendStartAdToBackend,
  ]);

  useEffect(() => {
    return () => {
      if (gamePunchStateRefreshTimerRef.current) {
        window.clearTimeout(gamePunchStateRefreshTimerRef.current);
      }
      // 언마운트 시 SWR 캐시 클리어 → 다음 마운트에서 반드시 서버 fresh 응답 적용
      mutateGamePunchState(undefined, { revalidate: false });
    };
  }, [mutateGamePunchState]);

  useEffect(() => {
    if (!freePunchHint && !burningHint) return undefined;
    const timer = window.setTimeout(() => {
      setFreePunchHint('');
      setBurningHint('');
    }, 1400);
    return () => window.clearTimeout(timer);
  }, [freePunchHint, burningHint]);

  useEffect(() => {
    if (!burningStartEffectEvent?.id) {
      setBurningStartTextPhase(0);
      return undefined;
    }
    setBurningStartTextPhase(0);
    const timer = window.setTimeout(() => {
      setBurningStartTextPhase(1);
    }, BURNING_START_TEXT_SWITCH_MS);
    return () => window.clearTimeout(timer);
  }, [burningStartEffectEvent?.id]);

  useEffect(() => {
    document.getElementsByClassName('page-container')[0]?.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let canceled = false;

    Promise.all(PUNCH_CRITICAL_PRELOAD_IMAGES.map((src) => preloadImage(src))).finally(() => {
      if (canceled) return;
      setIsPunchCoreAssetsReady(true);
    });

    PUNCH_OPTIONAL_PRELOAD_IMAGES.forEach((src) => {
      void preloadImage(src);
    });

    return () => {
      canceled = true;
    };
  }, []);

  const handlePunchWithAssetGuard = useCallback(() => {
    if (!isPunchCoreAssetsReady) return;
    playPunchSfx(isBurningActive ? 'burning' : 'normal');
    handlePunch();
  }, [handlePunch, isBurningActive, isPunchCoreAssetsReady, playPunchSfx]);

  const triggerScreenShake = useCallback((cls, durationMs) => {
    if (!cls || durationMs <= 0) return;
    if (shakeRafRef.current) {
      window.cancelAnimationFrame(shakeRafRef.current);
      shakeRafRef.current = null;
    }
    if (shakeResetTimerRef.current) {
      window.clearTimeout(shakeResetTimerRef.current);
      shakeResetTimerRef.current = null;
    }

    setActiveShakeClass('');
    shakeRafRef.current = window.requestAnimationFrame(() => {
      setActiveShakeClass(cls);
      shakeRafRef.current = null;
    });
    shakeResetTimerRef.current = window.setTimeout(() => {
      setActiveShakeClass('');
      shakeResetTimerRef.current = null;
    }, durationMs);
  }, []);

  useEffect(() => {
    const onSpacePunch = (event) => {
      const isSpace = event.code === 'Space' || event.key === ' ';
      if (!isSpace) return;
      if (burningStartEffectEvent) return;

      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();
      const isTypingTarget =
        tagName === 'input' || tagName === 'textarea' || target?.isContentEditable || tagName === 'select';
      if (isTypingTarget) return;

      event.preventDefault();
      handlePunchWithAssetGuard();
    };

    window.addEventListener('keydown', onSpacePunch);
    return () => window.removeEventListener('keydown', onSpacePunch);
  }, [burningStartEffectEvent, handlePunchWithAssetGuard]);
  useEffect(() => {
    if (!burningMilestoneEvent?.id) return undefined;
    const cls = getMilestoneShakeClassByHit(burningMilestoneEvent.hit);
    triggerScreenShake(cls, 560);
    return undefined;
  }, [burningMilestoneEvent, triggerScreenShake]);

  useEffect(() => {
    if (!rewardBurstEvent?.id || !rewardBurstEvent.isBurning) return undefined;
    triggerScreenShake('punch-screen-shake-hit', 260);
    return undefined;
  }, [rewardBurstEvent?.id, rewardBurstEvent?.isBurning, triggerScreenShake]);

  useEffect(() => {
    if (!rewardBurstEvent?.id) return undefined;
    if ((rewardBurstEvent.rewardPoint ?? 0) < HIGH_REWARD_THRESHOLD.BASIC) return undefined;

    const cls = getHighRewardShakeClassByPoint(rewardBurstEvent.rewardPoint ?? 0);
    triggerScreenShake(cls, 520);
    return undefined;
  }, [rewardBurstEvent?.id, rewardBurstEvent?.rewardPoint, triggerScreenShake]);

  useEffect(() => {
    if (!rewardBurstEvent?.id) return;
    const point = rewardBurstEvent.rewardPoint ?? 0;
    if (point < HIGH_REWARD_THRESHOLD.BASIC) return;
    playHighRewardSfx(point);
  }, [rewardBurstEvent?.id, rewardBurstEvent?.rewardPoint, playHighRewardSfx]);

  useEffect(() => {
    if (activePopup?.type !== POPUP_TYPE.HIGH_REWARD) return undefined;
    const cls = getHighRewardShakeClassByPoint(activePopup.rewardPoint ?? 0);
    triggerScreenShake(cls, 520);
    return undefined;
  }, [activePopup, triggerScreenShake]);

  useEffect(
    () => () => {
      if (shakeRafRef.current) {
        window.cancelAnimationFrame(shakeRafRef.current);
      }
      if (shakeResetTimerRef.current) {
        window.clearTimeout(shakeResetTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!rewardBurstEvent?.id) return;
    const variant = getRewardEffectVariant(rewardBurstEvent.rewardPoint);
    if (!variant) return;
    setMainRewardBackdropEffect({ id: `${rewardBurstEvent.id}-${variant}`, variant });
  }, [rewardBurstEvent?.id, rewardBurstEvent?.rewardPoint]);

  useEffect(() => {
    if (!mainRewardBackdropEffect?.id) return undefined;
    const currentEffectId = mainRewardBackdropEffect.id;
    const timer = window.setTimeout(() => {
      setMainRewardBackdropEffect((prev) => (prev?.id === currentEffectId ? null : prev));
    }, MAIN_REWARD_BACKDROP_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [mainRewardBackdropEffect?.id]);

  const onUseFreePunch = () => {
    if (punchAdLoadingPlacement) return;
    const result = handleUseFreePunch();
    if (!result?.ok) {
      if (result.reason === 'COOLDOWN') {
        setFreePunchHint('무료펀치 쿨타임 진행 중이에요');
      } else if (result.reason === 'ALREADY_READY') {
        setFreePunchHint('무료펀치가 준비되어 있어요. 고양이를 터치하세요');
      }
      return;
    }
    handleFreePunchAdWatch();
  };
  const isFreePunchSoldOut = freePunchRemain <= 0;
  const isFreePunchCooldown = !isFreePunchSoldOut && freePunchCooldownSec > 0;
  const freePunchLabel = `${freePunchRemain}/${freePunchDailyMax}`;
  const freePunchCooldownLabel = isFreePunchCooldown ? formatMmSs(freePunchCooldownSec) : '';
  const freePunchValueLabel = isFreePunchSoldOut
    ? '오늘 모두 소진!'
    : isFreePunchCooldown
      ? freePunchCooldownLabel
      : freePunchLabel;

  const onUseBurning = () => {
    if (punchAdLoadingPlacement) return;
    const result = handleUseBurning();
    if (!result?.ok) {
      if (result.reason === 'COOLDOWN') {
        setBurningHint('버닝타임 쿨타임 진행 중이에요');
      } else if (result.reason === 'ALREADY_ACTIVE') {
        setBurningHint('버닝타임 진행 중이에요');
      }
      return;
    }
    handleWatchAdForBurningStartWithRefresh();
  };

  const handleWatchAdForRestWithRefresh = useCallback(() => {
    if (punchAdLoadingPlacement) return;
    if (!isPunchMockModeEnabled && RTE === 'APP') {
      triggerPunchAd('mandatory20', () => {
        handleWatchAdForRest();
        scheduleGamePunchStateRefresh();
      });
      return;
    }
    handleWatchAdForRest();
    scheduleGamePunchStateRefresh();
  }, [
    RTE,
    handleWatchAdForRest,
    isPunchMockModeEnabled,
    punchAdLoadingPlacement,
    scheduleGamePunchStateRefresh,
    triggerPunchAd,
  ]);

  const handleWatchAdForHighRewardWithRefresh = useCallback(() => {
    if (punchAdLoadingPlacement) return;
    if (!isPunchMockModeEnabled && RTE === 'APP') {
      triggerPunchAd('highReward', () => {
        handleWatchAdForHighReward();
        scheduleGamePunchStateRefresh();
      });
      return;
    }
    handleWatchAdForHighReward();
    scheduleGamePunchStateRefresh();
  }, [
    RTE,
    handleWatchAdForHighReward,
    isPunchMockModeEnabled,
    punchAdLoadingPlacement,
    scheduleGamePunchStateRefresh,
    triggerPunchAd,
  ]);

  const handleSkipHighRewardWithRefresh = useCallback(() => {
    if (punchAdLoadingPlacement) return;
    handleSkipHighReward();
    scheduleGamePunchStateRefresh();
  }, [handleSkipHighReward, punchAdLoadingPlacement, scheduleGamePunchStateRefresh]);

  const handleWatchAdForBurningStartWithRefresh = useCallback(() => {
    if (punchAdLoadingPlacement) return;
    if (!isPunchMockModeEnabled) {
      triggerPunchAd('burningEnter', (completeResult) => {
        handleWatchAdForBurningStart(completeResult);
        scheduleGamePunchStateRefresh(500);
      });
      return;
    }
    handleWatchAdForBurningStart();
    scheduleGamePunchStateRefresh(500);
  }, [
    handleWatchAdForBurningStart,
    isPunchMockModeEnabled,
    punchAdLoadingPlacement,
    scheduleGamePunchStateRefresh,
    triggerPunchAd,
  ]);

  const handleWatchAdForBurningEndWithRefresh = useCallback(() => {
    if (punchAdLoadingPlacement) return;
    if (!isPunchMockModeEnabled && RTE === 'APP') {
      triggerPunchAd('burningExit', () => {
        handleWatchAdForBurningEnd();
        scheduleGamePunchStateRefresh();
      });
      return;
    }
    handleWatchAdForBurningEnd();
    scheduleGamePunchStateRefresh();
  }, [
    RTE,
    handleWatchAdForBurningEnd,
    isPunchMockModeEnabled,
    punchAdLoadingPlacement,
    scheduleGamePunchStateRefresh,
    triggerPunchAd,
  ]);

  const handleSkipBurningEndAdWithRefresh = useCallback(() => {
    if (punchAdLoadingPlacement) return;
    handleSkipBurningEndAd();
    scheduleGamePunchStateRefresh();
  }, [handleSkipBurningEndAd, punchAdLoadingPlacement, scheduleGamePunchStateRefresh]);

  const handleWatchAdForPointShortageWithRefresh = useCallback(() => {
    if (punchAdLoadingPlacement) return;
    if (!isPunchMockModeEnabled && RTE === 'APP') {
      triggerPunchAd('freePunchShortage', () => {
        handleWatchAdForPointShortage();
        scheduleGamePunchStateRefresh();
      });
      return;
    }
    handleWatchAdForPointShortage();
    scheduleGamePunchStateRefresh();
  }, [
    RTE,
    handleWatchAdForPointShortage,
    isPunchMockModeEnabled,
    punchAdLoadingPlacement,
    scheduleGamePunchStateRefresh,
    triggerPunchAd,
  ]);

  const handleChargeForPointShortageWithRefresh = useCallback(() => {
    handleChargeForPointShortage();
    scheduleGamePunchStateRefresh();
  }, [handleChargeForPointShortage, scheduleGamePunchStateRefresh]);

  const burningLabel = `${burningRemain}/${burningDailyMax}`;
  const isBurningCooldown = !isBurningActive && burningCooldownSec > 0;
  const burningCooldownLabel = isBurningActive
    ? formatMmSs(burningDurationSec)
    : burningCooldownSec > 0
      ? formatMmSs(burningCooldownSec)
      : '';
  const burningValueLabel = isBurningActive || isBurningCooldown ? burningCooldownLabel : burningLabel;
  const isBurningTenHitGoldActive = burningHitCount > 0 && burningHitCount % 10 === 0;
  const sectionBackgroundStyle = isBurningActive
    ? {
        backgroundColor: '#1E1E1E',
      }
    : undefined;
  const shouldShowBurningStarField = isBurningActive || !!mainRewardBackdropEffect;
  const burningStarGlowColor = (() => {
    if (!mainRewardBackdropEffect?.variant) return 'rgba(255, 42.66, 240.84, 0.40)';
    if (mainRewardBackdropEffect.variant === 'emerald') return 'rgba(125, 255, 208, 0.55)';
    if (mainRewardBackdropEffect.variant === 'gold') return 'rgba(255, 216, 137, 0.58)';
    if (mainRewardBackdropEffect.variant === 'purple') return 'rgba(201, 160, 255, 0.58)';
    return 'rgba(255, 255, 255, 0.62)';
  })();

  const activeInfoSheet = activeInfoSheetType ? INFO_SHEET_CONTENT[activeInfoSheetType] : null;
  const burningStartPrimaryText = burningStartTextPhase === 0 ? '30초간' : '400 HIT';
  const burningStartSecondaryText = burningStartTextPhase === 0 ? '광고 스킵 100%' : '도전하세요!!';
  const floatingToastPoint = Number(floatingToastEvent?.text?.match(/(\d+)P/)?.[1] ?? 0);

  const handleResetPunchMockStorage = () => {
    try {
      window.localStorage.removeItem(PUNCH_MOCK_STORAGE_KEY);
    } catch (error) {
      // no-op: localStorage 접근 제한 환경에서도 페이지 리로드는 수행
    }
    window.location.reload();
  };
  const handleTogglePunchApiMode = () => {
    if (!SHOW_PUNCH_MODE_DEBUG_UI) return;
    const nextMockMode = !isPunchMockModeEnabled;
    try {
      window.localStorage.setItem(PUNCH_API_MODE_STORAGE_KEY, nextMockMode ? 'mock' : 'api');
    } catch (error) {
      // no-op
    }
    setIsPunchMockModeEnabled(nextMockMode);
    window.location.reload();
  };

  const benefits = [
    {
      ...displayData.benefits[0],
      value: { label: freePunchValueLabel, onClick: onUseFreePunch },
      onInfoClick: () => setActiveInfoSheetType(INFO_SHEET_TYPE.FREE_PUNCH),
      tone: `${displayData.benefits[0].tone} clickable free-punch`,
      disabled: isFreePunchCooldown || isFreePunchSoldOut || Boolean(punchAdLoadingPlacement),
      isValueLoading: punchAdLoadingPlacement === 'freePunch',
      adIconSrc: isFreePunchCooldown || isFreePunchSoldOut ? null : PUNCH_CARD_AD_ICON_URL,
    },
    {
      ...displayData.benefits[1],
      value: { label: burningValueLabel, onClick: onUseBurning },
      onInfoClick: () => setActiveInfoSheetType(INFO_SHEET_TYPE.BURNING),
      tone: `${displayData.benefits[1].tone} clickable burning`,
      disabled: isBurningActive || burningRemain <= 0 || burningCooldownSec > 0 || Boolean(punchAdLoadingPlacement),
      isValueLoading: punchAdLoadingPlacement === 'burningEnter',
      adIconSrc: isBurningActive || isBurningCooldown ? null : PUNCH_CARD_AD_ICON_URL,
    },
    {
      ...displayData.benefits[2],
      value: { label: displayData.benefits[2].value, onClick: () => navigate('/punch/prize-mission') },
      tone: `${displayData.benefits[2].tone} clickable prize-mission`,
    },
  ];

  const activePopupAdPlacement =
    activePopup && (activePopup.type !== POPUP_TYPE.POINT_SHORTAGE || activePopup.adAvailable)
      ? (PUNCH_POPUP_AD_PLACEMENT_BY_TYPE[activePopup.type] ?? null)
      : null;
  const isActivePopupAdLoading = Boolean(activePopupAdPlacement && punchAdLoadingPlacement === activePopupAdPlacement);

  return (
    <section
      className={`relative min-h-screen overflow-x-hidden pb-[4rem] ${
        isBurningActive ? '' : 'bg-gradient-to-b from-background01 to-[#CDC6FF]'
      }`}
      style={sectionBackgroundStyle}
    >
      <div className={`relative z-[2] ${activeShakeClass}`}>
        {burningStartEffectEvent ? (
          <div
            className="fixed inset-0 z-[45] flex items-center justify-center"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <div
              key={burningStartEffectEvent.id}
              className="flex min-h-screen w-full items-center justify-center bg-black/80 px-4"
            >
              <div className="relative flex h-[360px] w-[360px] items-center justify-center">
                <img
                  src={BurningStartSparkleImage}
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute h-[263px] w-[263px] object-contain"
                  style={{ left: '97px', top: '-80px' }}
                />
                <img
                  src={BurningStartCenterLightImage}
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute left-1/2 top-1/2 h-[360px] w-[360px] -translate-x-1/2 -translate-y-1/2 object-contain"
                />
                <div className="relative z-[1] flex flex-col items-center mt-[3.125rem]">
                  <img
                    src={BurningStartCoinImage}
                    alt=""
                    aria-hidden="true"
                    className="h-[72px] w-[72px] object-contain"
                  />
                  <p
                    key={`burning-start-primary-${burningStartTextPhase}`}
                    className="m-0 bg-clip-text text-center font-['Pretendard'] text-[40px] font-black leading-[1.35] tracking-[-1px] text-transparent"
                    style={{
                      backgroundImage:
                        'linear-gradient(180deg, #CE7F00 29.327%, #FFFFFF 51.923%, #8B4500 60.577%, #F39600 74.519%)',
                      textShadow: '0 0 10px rgba(255, 173, 21, 0.46)',
                      animation: 'punchBurningStartTextFade 820ms ease-out both',
                    }}
                  >
                    {burningStartPrimaryText}
                  </p>
                  <p
                    key={`burning-start-secondary-${burningStartTextPhase}`}
                    className="m-0 text-center font-['Pretendard'] text-[22px] font-bold leading-[1.4] tracking-[-0.4px] text-white"
                    style={{ animation: 'punchBurningStartTextFade 820ms ease-out both' }}
                  >
                    {burningStartSecondaryText}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <PunchTopBar
          isBurningActive={isBurningActive}
          onOpenGuide={() => setActiveInfoSheetType(INFO_SHEET_TYPE.GAME)}
        />
        <PunchWinnerTicker
          isPunchMockModeEnabled={isPunchMockModeEnabled}
          mockWinners={displayData.winners}
          accessToken={accessToken}
          API_SERVER_DOMAIN={API_SERVER_DOMAIN}
          fetchWithToken={fetchWithToken}
        />

        <main className="relative flex min-h-[calc(100vh-94px)] w-full box-border flex-col items-center overflow-hidden px-[10px] pb-12">
          <PunchMainRewardBackdrop effect={mainRewardBackdropEffect} />
          {shouldShowBurningStarField ? <BurningStarField glowColor={burningStarGlowColor} /> : null}
          <div className="relative z-[3] flex w-full flex-col items-center">
            <PunchPrizePoolSummary
              isBurningActive={isBurningActive}
              isPunchMockModeEnabled={isPunchMockModeEnabled}
              summary={displayData.summary}
              stateMeta={gamePunchState}
              livePoolAmountFromPlay={livePrizePoolAmountFromPlay}
              accessToken={accessToken}
              API_SERVER_DOMAIN={API_SERVER_DOMAIN}
              fetchWithToken={fetchWithToken}
            />

            <section
              className={`relative mt-5 flex h-[280px] w-[280px] items-center justify-center outline-none focus:outline-none focus-visible:outline-none ${
                isPunchCoreAssetsReady ? 'cursor-pointer' : 'cursor-wait'
              }`}
              aria-label="펀치 캐릭터 영역"
              role="button"
              tabIndex={0}
              onClick={handlePunchWithAssetGuard}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  if (burningStartEffectEvent) return;
                  handlePunchWithAssetGuard();
                }
              }}
            >
              {isBurningActive ? (
                <div
                  className="absolute inset-0 z-[-1] rounded-[9999px] pointer-events-none"
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'rgba(255, 43, 241, 0.40)',
                    borderRadius: 9999,
                    filter: 'blur(50px)',
                  }}
                />
              ) : (
                <div
                  className="absolute inset-0 z-[-1] rounded-[9999px]"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                  }}
                />
              )}
              {isBurningActive ? null : (
                <div
                  className="absolute inset-0 z-[-1] rounded-[9999px]"
                  style={{
                    boxShadow: 'inset 0 0 0 5px rgba(255,255,255,0.2)',
                  }}
                />
              )}
              {isBurningActive ? null : (
                <div
                  className="absolute inset-[5px] z-[-1] rounded-[9999px] border"
                  style={{ borderColor: 'rgba(255,255,255,0.35)' }}
                />
              )}
              <PunchRewardBurst
                event={rewardBurstEvent}
                className="left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2"
                effectGlowColor={mainRewardBackdropEffect ? burningStarGlowColor : null}
              />
              <PunchRewardRing
                key={mainRewardBackdropEffect?.id ?? 'ring-none'}
                variant={mainRewardBackdropEffect?.variant ?? null}
              />
              {isBurningActive ? (
                <div className="absolute right-[-0.5rem] top-[50px] z-[12] -translate-y-1/2">
                  <div
                    key={`burning-hit-${burningHitCount}`}
                    className="punch-hit-toast-enter flex w-[62px] flex-col items-center rounded-[50px] px-[10px] pb-[10px] pt-[16px] text-center"
                    style={{
                      border: `3px solid ${isBurningTenHitGoldActive ? '#D8A93A' : '#f6475f'}`,
                      backgroundImage:
                        'radial-gradient(31% 70% at 19% 16%, #848484 0%, #6b6b6b 25%, #515151 50%, #383838 75%, #1e1e1e 100%)',
                    }}
                  >
                    <div
                      className="h-[33px] min-w-full font-['Pretendard'] text-[24px] font-bold leading-[1.3]"
                      style={{ color: isBurningTenHitGoldActive ? '#D8A93A' : '#ffffff' }}
                    >
                      {burningHitCount}
                    </div>
                    <div
                      className="font-['Pretendard'] text-[14px] font-bold leading-[1.3]"
                      style={{ color: isBurningTenHitGoldActive ? '#D8A93A' : '#ffffff' }}
                    >
                      HIT !
                    </div>
                    <div className="mt-[2px] h-6 w-6 text-[22px] leading-6">🔥</div>
                  </div>
                </div>
              ) : null}
              <img
                src={isPunching ? PunchBackImage : PunchFrontImage}
                alt="CAT PUNCH"
                className={`absolute left-1/2 top-1/2 z-[12] h-[232px] w-[232px] -translate-x-1/2 -translate-y-1/2 object-contain ${
                  isPunching ? (isBurningActive ? 'punch-cat-shake-burning' : 'punch-cat-shake') : ''
                }`}
              />
              {/* 기존 하단 텍스트형 획득 토스트
              {floatingToastEvent ? (
                <div
                  key={floatingToastEvent.id}
                  className={`punch-reward-toast absolute bottom-3 text-center font-['Pretendard'] text-[17px] font-bold ${
                    isBurningActive
                      ? 'bg-transparent px-0 py-0 text-white'
                      : 'rounded-full bg-white/92 px-3 py-1 text-black'
                  }`}
                >
                  {floatingToastEvent.text}
                </div>
              ) : null}
              */}
              {floatingToastEvent ? (
                <div key={floatingToastEvent.id} className="punch-point-toast absolute left-[-30px] top-[25px] z-[12]">
                  <div
                    className="w-[62px] rounded-[50px] p-[3px]"
                    style={{
                      backgroundImage: 'linear-gradient(145deg, #f4efff 0%, #eae2ff 52%, #c8baf3 100%)',
                      boxShadow: '0 1px 0 rgba(255,255,255,0.7) inset, 0 -1px 0 rgba(144,121,203,0.55) inset',
                    }}
                  >
                    <div
                      className="flex w-full flex-col items-center rounded-[47px] px-[10px] pb-[10px] pt-[16px] text-center"
                      style={{
                        backgroundImage: 'linear-gradient(to bottom, #3B5CD2 0%, #8B68DD 100%)',
                      }}
                    >
                      <div className="h-[33px] min-w-full font-['Pretendard'] text-[24px] font-bold leading-[1.3] text-white">
                        {floatingToastPoint}
                      </div>
                      <div className="font-['Pretendard'] text-[14px] font-bold leading-[1.3] text-white">P획득</div>
                      <img src={ToastSparklesImage} alt="" className="mt-[2px] h-6 w-6 object-contain" />
                    </div>
                  </div>
                </div>
              ) : null}
            </section>

            <img
              src={isBurningActive ? BurningDividerLineImage : DividerLineImage}
              alt="구분선"
              className="mt-[10px] h-5 w-full object-contain"
            />

            <section className="relative z-[12] mt-3 grid w-full grid-cols-3 gap-[11px]" aria-label="펀치 혜택">
              {benefits.map((benefit) => (
                <div key={benefit.title} className="flex flex-col items-center">
                  <PunchBenefitCard
                    title={benefit.title}
                    value={benefit.value}
                    icon={benefit.icon}
                    tone={benefit.tone}
                    disabled={benefit.disabled}
                    onInfoClick={benefit.onInfoClick}
                    adIconSrc={benefit.adIconSrc}
                    isValueLoading={benefit.isValueLoading}
                  />
                </div>
              ))}
            </section>
            <p
              className={`mt-5 text-center font-['Pretendard'] text-xs font-normal leading-[16.8px] ${
                isBurningActive ? 'text-gray03' : 'text-black'
              }`}
            >
              야옹이를 터치해서 펀치를 쳐보세요!
              <br />
              {isBurningActive ? '버닝타임에선 게임 도중 광고가 등장하지 않아요.' : '펀치 1회당 5포인트가 소진돼요.'}
            </p>
            {SHOW_PUNCH_MODE_DEBUG_UI ? (
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleTogglePunchApiMode}
                  className="rounded-[8px] border-0 bg-white/85 px-3 py-1 font-['Pretendard'] text-[11px] font-bold leading-none text-gray04 shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
                >
                  {isPunchMockModeEnabled ? '목업 모드' : '실서버 모드'}
                </button>
                {isPunchMockModeEnabled ? (
                  <button
                    type="button"
                    onClick={handleResetPunchMockStorage}
                    className="rounded-[8px] border-0 bg-white/85 px-3 py-1 font-['Pretendard'] text-[11px] font-bold leading-none text-gray04 shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
                  >
                    초기화
                  </button>
                ) : null}
              </div>
            ) : null}
            {freePunchHint ? (
              <div className="mt-2 text-center font-['Pretendard'] text-xs font-semibold text-gray04">
                {freePunchHint}
              </div>
            ) : null}
            {burningHint ? (
              <div className="mt-2 text-center font-['Pretendard'] text-xs font-semibold text-gray04">
                {burningHint}
              </div>
            ) : null}
          </div>
        </main>
      </div>

      {activePopup ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 px-4">
          {activePopup.type === POPUP_TYPE.AD_REST ? (
            <PunchAdRestPopup
              isPrimaryAdLoading={isActivePopupAdLoading}
              onWatchAd={handleWatchAdForRestWithRefresh}
              onSkip={closeCurrentPopup}
            />
          ) : null}
          {activePopup.type === POPUP_TYPE.HIGH_REWARD ? (
            <PunchHighRewardPopup
              rewardPoint={activePopup.rewardPoint ?? 50}
              isPrimaryAdLoading={isActivePopupAdLoading}
              onWatchAd={handleWatchAdForHighRewardWithRefresh}
              onSkip={handleSkipHighRewardWithRefresh}
            />
          ) : null}
          {activePopup.type === POPUP_TYPE.POINT_SHORTAGE ? (
            <PunchPointShortagePopup
              adAvailable={!!activePopup.adAvailable}
              isPrimaryAdLoading={isActivePopupAdLoading}
              onWatchAd={handleWatchAdForPointShortageWithRefresh}
              onCharge={handleChargeForPointShortageWithRefresh}
              onClose={closeCurrentPopup}
            />
          ) : null}
          {activePopup.type === POPUP_TYPE.FREE_PUNCH_AD ? (
            <PunchFreePunchAdPopup
              isPrimaryAdLoading={isActivePopupAdLoading}
              onWatchAd={handleFreePunchAdWatch}
              onClose={closeCurrentPopup}
            />
          ) : null}
          {activePopup.type === POPUP_TYPE.BURNING_START_AD ? (
            <PunchBurningStartPopup
              isPrimaryAdLoading={isActivePopupAdLoading}
              onWatchAd={handleWatchAdForBurningStartWithRefresh}
              onClose={closeCurrentPopup}
            />
          ) : null}
          {activePopup.type === POPUP_TYPE.BURNING_END_AD ? (
            <PunchBurningEndPopup
              isPrimaryAdLoading={isActivePopupAdLoading}
              onWatchAd={handleWatchAdForBurningEndWithRefresh}
              onSkip={handleSkipBurningEndAdWithRefresh}
            />
          ) : null}
        </div>
      ) : null}

      <PunchInfoBottomSheet
        isOpen={!!activeInfoSheet}
        onClose={() => setActiveInfoSheetType(null)}
        title={activeInfoSheet?.title ?? ''}
        highlights={activeInfoSheet?.highlights ?? []}
        sections={activeInfoSheet?.sections ?? []}
        notices={activeInfoSheet?.notices ?? []}
      />

      <PunchBottomRewardToast events={bottomToastEvents} isBurningActive={isBurningActive} />

      <PunchBottomPoint point={currentPoint} />
    </section>
  );
}
